/**
 * The door as one document (D23): what the editor holds, and the request it sends.
 *
 * The server is the one judge of a door. It drops an empty slide and a question without
 * text, it checks every text with its one parser (`services/richtext.py`), and it says what
 * a submission still lacks. This module mirrors only the rules the editor has to know before
 * it asks:
 *
 * - which rows the server keeps, because an upload names its slot by the server's count
 *   (`slide:2`) and the form may hold an empty row in between;
 * - the few problems the request would otherwise answer with a sentence in English (a button
 *   without a label), or answer by quietly dropping what was typed (answers without a
 *   question).
 */
import { moveItem } from "./list.js";
/** The verdicts of the security check in words; a clean one reads "geprüft". */
export const SCAN_STATUS_LABELS = {
    pending: "Prüfung läuft",
    rejected: "abgelehnt",
    failed: "Prüfung fehlgeschlagen",
};
/** `MAX_SLIDES` and `MAX_QUESTIONS` in `services/bookings.py` (D23). */
export const MAX_SLIDES = 5;
export const MAX_QUESTIONS = 3;
/**
 * The four answer slots, in display order. A question shows three of them at first (D37) and
 * takes two to four; the slot keys stay fixed, so the answers keep their keys whichever mask
 * wrote them.
 */
export const OPTION_KEYS = ["a", "b", "c", "d"];
/** How many answer slots a question offers (D37): three at first, two to four. */
export const MIN_OPTIONS = 2;
export const DEFAULT_OPTIONS = 3;
export const MAX_OPTIONS = OPTION_KEYS.length;
/**
 * One name per input of the door, keyed by its field in the request: the editor's label, the
 * place of a refusal ("Folie 2, Adresse des Knopfs: …") and the review say the same words.
 */
export const FIELD_NAMES = {
    title: "Titel",
    body: "Text",
    label: "Beschriftung des Knopfs",
    url: "Adresse des Knopfs",
    intro: "Begrüßung",
    success_text: "Text, wenn alle Antworten richtig sind",
    failure_text: "Text, wenn nicht alle Antworten richtig sind",
    failure_title: "Überschrift nach falscher Antwort",
    retry_label: "Knopf nach falscher Antwort",
    failure_note: "Hinweis nach falscher Antwort",
    question_text: "Fragetext",
    answer_options: "Antworten",
    prize_description: "Gewinn",
    prize_quantity: "Anzahl der Gewinne",
    prize_digital: "Digitaler Gewinn (Zusendung per E-Mail)",
};
let rowCount = 0;
function rowKey() {
    rowCount += 1;
    return `r${String(rowCount)}`;
}
export function emptySlide() {
    return {
        key: rowKey(),
        title: "",
        body: "",
        media: null,
        textAlternative: "",
        needsNoCaptions: false,
        nextLabel: "",
        link: false,
        linkLabel: "",
        linkUrl: "",
    };
}
export function emptyQuestion() {
    return {
        key: rowKey(),
        questionId: null,
        text: "",
        options: { a: "", b: "", c: "", d: "" },
        correct: "",
        image: null,
        archived: false,
        optionCount: DEFAULT_OPTIONS,
    };
}
/**
 * The slots a stored question opens with: up to its last answer, at least two, and three for
 * a question without answers yet.
 */
function optionCountOf(options) {
    const last = OPTION_KEYS.reduce((found, key, index) => (options[key].trim() === "" ? found : index + 1), 0);
    return last === 0 ? DEFAULT_OPTIONS : Math.max(MIN_OPTIONS, last);
}
/** The server's `Slide.is_empty`: such a slide is no slide, and a save does not store it. */
export function isEmptySlide(slide) {
    return (slide.title.trim() === "" &&
        slide.body.trim() === "" &&
        slide.media === null &&
        slide.nextLabel.trim() === "" &&
        !slide.link);
}
/** A question without text is an empty slot of the form, and a save stores none for it. */
export function isEmptyQuestion(question) {
    return question.text.trim() === "";
}
/** The stored door, row by row. */
function storedForm(booking) {
    return {
        slides: booking.slides.map((slide) => ({
            key: rowKey(),
            title: slide.title ?? "",
            body: slide.body ?? "",
            media: slide.media ?? null,
            textAlternative: slide.media?.text_alternative ?? "",
            needsNoCaptions: slide.media?.needs_no_captions ?? false,
            nextLabel: slide.button?.label ?? "",
            link: slide.link != null,
            linkLabel: slide.link?.label ?? "",
            linkUrl: slide.link?.url ?? "",
        })),
        questions: booking.quiz.questions.map((question) => {
            const stored = question.answer_options ?? {};
            const options = {
                a: stored.a ?? "",
                b: stored.b ?? "",
                c: stored.c ?? "",
                d: stored.d ?? "",
            };
            return {
                key: rowKey(),
                questionId: question.question_id,
                text: question.question_text,
                options,
                correct: question.correct_option_key ?? "",
                image: question.image ?? null,
                archived: question.is_archived,
                optionCount: optionCountOf(options),
            };
        }),
        intro: booking.quiz.intro ?? "",
        successText: booking.quiz.success_text ?? "",
        failureText: booking.quiz.failure_text ?? "",
        failureTitle: booking.quiz.failure_title ?? "",
        retryLabel: booking.quiz.retry_label ?? "",
        failureNote: booking.quiz.failure_note ?? "",
        prizeDescription: booking.prize_description ?? "",
        prizeQuantity: String(booking.prize_quantity),
        prizeDigital: booking.prize_digital,
        prizeImage: booking.prize_image,
    };
}
/** What the editor opens with: the stored door, with an empty slide and question where it has none. */
export function formOf(booking) {
    const form = storedForm(booking);
    return {
        ...form,
        slides: form.slides.length > 0 ? form.slides : [emptySlide()],
        questions: form.questions.length > 0 ? form.questions : [emptyQuestion()],
    };
}
/**
 * The door the server answered with, as the form, with what the form showed kept in place.
 *
 * The server keeps the order of what it stores and drops only empty rows, so the n-th stored
 * slide is the n-th slide the form had with content. That row's key carries over, and with it
 * the focus, a row's message and a picture shown from this session. The form's empty rows
 * come back in their places: dropping them would take away a slide somebody has just added
 * and not filled yet. `keep` names the empty slide an upload has just given a picture.
 */
export function adopt(previous, booking, keep) {
    const stored = storedForm(booking);
    return {
        ...stored,
        slides: merged(previous.slides, stored.slides, (slide) => slide.key !== keep && isEmptySlide(slide)),
        // A slot opened and left empty stays open: the server stores no empty answer.
        questions: merged(previous.questions, stored.questions, isEmptyQuestion, (local, row) => ({
            ...row,
            optionCount: Math.max(local.optionCount, row.optionCount),
        })),
    };
}
function merged(local, stored, empty, carry = (_, row) => row) {
    const kept = local.filter((row) => !empty(row));
    const rows = stored.map((row, index) => {
        const before = kept[index];
        return before === undefined ? row : { ...carry(before, row), key: before.key };
    });
    local.forEach((row, index) => {
        if (empty(row)) {
            rows.splice(index, 0, row);
        }
    });
    return rows;
}
/**
 * The save request: the whole door, every row in the form's order (D23).
 *
 * Empty rows travel too, and the server drops them. That keeps the place of a refusal
 * (`slides.2.body`) the place the form shows.
 */
export function documentOf(form, position) {
    return {
        field_position: position,
        slides: form.slides.map((slide) => ({
            title: slide.title.trim() || null,
            body: slide.body.trim() || null,
            media: slide.media === null
                ? null
                : {
                    asset_id: slide.media.asset_id,
                    poster_asset_id: slide.media.poster_asset_id ?? null,
                    captions_asset_id: slide.media.captions?.asset_id ?? null,
                    needs_no_captions: slide.needsNoCaptions,
                    // A video's text version as typed, an emptied one too; a picture has none, and
                    // null leaves the server's answer alone.
                    text_alternative: isVideo(slide.media) ? slide.textAlternative.trim() : null,
                },
            button: slide.nextLabel.trim() === "" ? null : { label: slide.nextLabel.trim(), action: "next", url: null },
            link: slide.link
                ? { label: slide.linkLabel.trim(), action: "link", url: slide.linkUrl.trim() || null }
                : null,
        })),
        quiz: {
            intro: form.intro.trim() || null,
            success_text: form.successText.trim() || null,
            failure_text: form.failureText.trim() || null,
            failure_title: form.failureTitle.trim() || null,
            retry_label: form.retryLabel.trim() || null,
            failure_note: form.failureNote.trim() || null,
            questions: form.questions.map((question) => ({
                question_id: question.questionId,
                question_text: question.text.trim(),
                // An empty slot is a deleted answer, and the server drops a correct key that no
                // longer names one: `missing` then asks for it again.
                answer_options: Object.fromEntries(OPTION_KEYS.filter((key) => question.options[key].trim() !== "").map((key) => [
                    key,
                    question.options[key].trim(),
                ])),
                correct_option_key: question.correct || null,
                image_asset_id: question.image?.asset_id ?? null,
            })),
        },
        // Sent as "", not null: the whole door is sent, and null would keep the stored text.
        prize_description: form.prizeDescription.trim(),
        prize_quantity: Number(form.prizeQuantity.trim()),
        prize_digital: form.prizeDigital,
    };
}
/** The id of an editor input, also what a {@link Problem} points at. */
export function fieldId(position, part, row) {
    const door = `tuer-${String(position)}`;
    return row === undefined ? `${door}-${part}` : `${door}-${row}-${part}`;
}
/**
 * What the form can say before it sends: where the request would be refused with a sentence
 * nobody here wrote, or where a save would quietly drop what was typed.
 *
 * Not a second rulebook. The texts are checked by the one parser on the server and
 * completeness by the submission; this covers only what the form itself puts together.
 */
export function problemsOf(form, position) {
    const found = [];
    form.slides.forEach((slide, index) => {
        const place = `Folie ${String(index + 1)}`;
        if (slide.link && slide.linkLabel.trim() === "") {
            found.push({
                field: fieldId(position, "link-text", slide.key),
                message: `${place}: Der Link-Knopf braucht eine Beschriftung.`,
                step: { kind: "slide", key: slide.key },
                element: "button",
            });
        }
        const url = slide.linkUrl.trim();
        if (slide.link && url !== "" && !isHttpsAddress(url)) {
            found.push({
                field: fieldId(position, "knopf-adresse", slide.key),
                message: `${place}: Links brauchen eine https-Adresse ohne Leer- und Anführungszeichen.`,
                step: { kind: "slide", key: slide.key },
                element: "button",
            });
        }
    });
    form.questions.forEach((question, index) => {
        const typed = OPTION_KEYS.some((key) => question.options[key].trim() !== "") || question.image !== null;
        if (isEmptyQuestion(question) && typed) {
            found.push({
                field: fieldId(position, "frage", question.key),
                message: `Frage ${String(index + 1)}: Der Fragetext fehlt. Ohne ihn wird die Frage nicht gespeichert.`,
                step: { kind: "quiz" },
            });
        }
        // The server stores a question without its right answer, and the door would admit every
        // answer to the draw (`correct_option_key`): the mark is asked before the save (24.10.2026).
        const filled = OPTION_KEYS.filter((key) => question.options[key].trim() !== "");
        if (!isEmptyQuestion(question) && !filled.some((key) => key === question.correct)) {
            const first = filled[0];
            found.push({
                field: fieldId(position, first === undefined ? "antwort-a" : `richtig-${first}`, question.key),
                message: first === undefined
                    ? `Frage ${String(index + 1)}: Tragen Sie die Antworten ein und markieren Sie die richtige.`
                    : `Frage ${String(index + 1)}: Markieren Sie die richtige Antwort.`,
                step: { kind: "quiz" },
            });
        }
    });
    const quantity = form.prizeQuantity.trim();
    if (!/^\d+$/.test(quantity) || Number(quantity) < 1 || Number(quantity) > 1000) {
        found.push({
            field: fieldId(position, "anzahl"),
            message: `${FIELD_NAMES.prize_quantity}: eine ganze Zahl von 1 bis 1000.`,
            step: { kind: "right" },
        });
    }
    return found;
}
/**
 * Where to fix a part the server last said is missing, found in the form as it is now: the
 * server's answer may be older than the form. `null` when no row of the form lacks it any more.
 */
export function missingTarget(form, key, position) {
    const onSlide = (lacks, part, element) => {
        const slide = form.slides.find(lacks);
        return slide === undefined ? null : { step: { kind: "slide", key: slide.key }, field: part(slide.key), element };
    };
    const onQuestion = (lacks) => {
        const question = form.questions.find(lacks);
        return question === undefined ? null : { step: { kind: "quiz" }, field: fieldId(position, "frage", question.key) };
    };
    const video = (slide) => isVideo(slide.media);
    const answers = (question) => OPTION_KEYS.filter((option) => question.options[option].trim() !== "");
    switch (key) {
        case "slides":
            // The server's test: no slide has a title or a text. Then the first one gets it.
            return form.slides.some((slide) => slide.title.trim() !== "" || slide.body.trim() !== "")
                ? null
                : onSlide(() => true, (row) => fieldId(position, "titel", row), "title");
        case "slide_button_url":
            return onSlide((slide) => slide.link && slide.linkUrl.trim() === "", (row) => fieldId(position, "knopf-adresse", row), "button");
        case "video_poster":
            return onSlide((slide) => video(slide) && (slide.media?.poster ?? null) === null, (row) => `${fieldId(position, "vorschaubild", row)}-datei`, "image");
        case "video_text":
            return onSlide((slide) => video(slide) && slide.textAlternative.trim() === "", (row) => fieldId(position, "textfassung", row), "image");
        case "video_captions":
            return onSlide((slide) => video(slide) && (slide.media?.captions ?? null) === null && !slide.needsNoCaptions, (row) => fieldId(position, "ohne-untertitel", row), "image");
        case "prize_description":
            return form.prizeDescription.trim() === ""
                ? { step: { kind: "right" }, field: fieldId(position, "gewinn"), element: "text" }
                : null;
        case "prize_quantity":
            return Number(form.prizeQuantity.trim()) >= 1 ? null : { step: { kind: "right" }, field: fieldId(position, "anzahl") };
        case "sponsor_question":
            return onQuestion((question) => question.archived);
        case "answer_options":
            return onQuestion((question) => !isEmptyQuestion(question) && answers(question).length < MIN_OPTIONS);
        case "correct_option_key":
            return onQuestion((question) => !isEmptyQuestion(question) && !answers(question).includes(question.correct));
    }
}
/** The weaker half of `richtext.is_https_url`; the server refuses the rest with its own sentence. */
function isHttpsAddress(value) {
    try {
        const url = new URL(value);
        return url.protocol === "https:" && url.hostname !== "";
    }
    catch {
        return false;
    }
}
/**
 * Where an upload goes once the form is saved (the editor saves before it uploads).
 *
 * The server counts only what it stores: slides with content, questions with a text. A
 * picture for an empty slide opens a new slide at the end, which then has to move to where
 * the form shows it. A question without text has no slot, there is no question yet to hang
 * the picture on, and a poster or caption file needs its slide's video.
 */
export function uploadSlot(form, target) {
    if (target.kind === "prize") {
        return { slot: "prize" };
    }
    if (target.kind === "question") {
        const kept = form.questions.filter((question) => !isEmptyQuestion(question));
        const index = kept.findIndex((question) => question.key === target.key);
        return index < 0 ? null : { slot: `question:${String(index)}` };
    }
    const at = form.slides.findIndex((slide) => slide.key === target.key);
    const slide = form.slides[at];
    if (slide === undefined) {
        return null;
    }
    const before = form.slides.slice(0, at).filter((row) => !isEmptySlide(row)).length;
    if (target.kind === "poster" || target.kind === "captions") {
        return isVideo(slide.media) ? { slot: `${target.kind}:${String(before)}` } : null;
    }
    if (!isEmptySlide(slide)) {
        return { slot: `slide:${String(before)}` };
    }
    const kept = form.slides.filter((row) => !isEmptySlide(row)).length;
    return kept === before
        ? { slot: `slide:${String(kept)}` }
        : { slot: `slide:${String(kept)}`, moveTo: before };
}
/**
 * The save that moves the slide an upload opened at the end to its place. Slides only: a
 * part that is not sent stays as stored, and the quiz has not changed.
 */
export function moveRequest(booking, to) {
    const slides = documentOf(storedForm(booking), booking.field_position).slides ?? [];
    return {
        field_position: booking.field_position,
        slides: moveItem(slides, slides.length - 1, to),
    };
}
/**
 * Put a picture into a slot of the door: save the form first when it has changes, upload into
 * the slot the server counts ({@link uploadSlot}), and move a slide the upload opened at the
 * end to where the form shows it.
 *
 * Should that move fail, the slide stays at the end and the form's row goes there too. Left in
 * place, the row would take the key of the slide after it, and each row would show the
 * neighbour's picture and focus.
 */
export async function uploadPicture(form, position, target, dirty, writes, 
/** What went up, for the answer: "Bild", "Video", "Vorschaubild", "Untertitel". */
what = "Bild") {
    const plan = uploadSlot(form, target);
    if (plan === null) {
        return {
            outcome: { ok: false, message: "Für diesen Platz lässt sich noch keine Datei hochladen." },
        };
    }
    let base = form;
    let saved;
    if (dirty) {
        const result = await writes.save(documentOf(form, position));
        if (!result.ok) {
            return { outcome: result };
        }
        base = adopt(form, result.booking);
        saved = base;
    }
    const uploaded = await writes.upload(plan.slot);
    if (!uploaded.ok) {
        return { outcome: uploaded, form: saved };
    }
    const keep = target.kind === "slide" ? target.key : undefined;
    let view = uploaded.booking;
    if (plan.moveTo !== undefined) {
        const moved = await writes.save(moveRequest(view, plan.moveTo));
        if (!moved.ok) {
            const at = base.slides.findIndex((slide) => slide.key === keep);
            base = { ...base, slides: moveItem(base.slides, at, base.slides.length - 1) };
            return {
                outcome: { ok: true, message: `${what} hochgeladen, die Folie steht aber am Ende. ${moved.message}` },
                form: adopt(base, view, keep),
                partial: true,
            };
        }
        view = moved.booking;
    }
    return { outcome: { ok: true, message: `${what} hochgeladen.` }, form: adopt(base, view, keep) };
}
/** The file a slot holds in the form. */
export function pictureAt(form, target) {
    if (target.kind === "prize") {
        return form.prizeImage;
    }
    if (target.kind === "question") {
        return form.questions.find((question) => question.key === target.key)?.image ?? null;
    }
    const media = form.slides.find((slide) => slide.key === target.key)?.media ?? null;
    if (target.kind === "poster") {
        return media?.poster ?? null;
    }
    return target.kind === "captions" ? (media?.captions ?? null) : media;
}
/** Whether a slide's stored file is a video (package H). */
export function isVideo(media) {
    return media?.kind === "video";
}
/**
 * JPEG, PNG, WebP and SVG, the formats the upload accepts (`detect_format` in
 * `services/assets.py`; an SVG only without script or outside references, D44).
 */
export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/svg+xml"];
/**
 * `max_upload_bytes` of the API, 16 MiB. The server measures the bytes again and answers
 * with its own sentence, so a lower limit there still says what is wrong.
 */
export const MAX_IMAGE_BYTES = 16 * 1024 * 1024;
/** The MP4 a slide takes (package H, `_MP4_BRANDS` in `services/assets.py`). */
export const VIDEO_TYPES = ["video/mp4"];
/** `max_video_upload_bytes` of the API, 48 MiB. */
export const MAX_VIDEO_BYTES = 48 * 1024 * 1024;
/** `CAPTIONS_MAX_BYTES` of the API. */
export const MAX_CAPTIONS_BYTES = 1024 * 1024;
export function isVideoFile(file) {
    return VIDEO_TYPES.includes(file.type);
}
/** Browsers report a `.vtt` file as `text/vtt` or with no type at all. */
function isCaptionsFile(file) {
    return file.type === "text/vtt" || (file.name ?? "").toLowerCase().endsWith(".vtt");
}
/**
 * Why a chosen file will not be taken, before a byte travels; `null` when it may.
 *
 * The browser's type comes from the file name, the server's from the content: a renamed file
 * passes here and is refused there, with the server's sentence.
 */
export function imageProblem(file) {
    return fileProblem(file, "picture");
}
/** {@link imageProblem} for any slot: a slide also takes an MP4, a caption slot a `.vtt`. */
export function fileProblem(file, accepts) {
    if (accepts === "captions") {
        if (!isCaptionsFile(file)) {
            return "Nur eine Untertiteldatei (.vtt).";
        }
        return file.size > MAX_CAPTIONS_BYTES ? "Die Datei ist größer als 1 MB." : null;
    }
    if (accepts === "slide" && isVideoFile(file)) {
        return file.size > MAX_VIDEO_BYTES ? "Das Video ist größer als 48 MB." : null;
    }
    if (!IMAGE_TYPES.includes(file.type)) {
        return accepts === "slide"
            ? "Nur JPEG, PNG, WebP, SVG oder ein Video im Format MP4."
            : "Nur JPEG, PNG, WebP oder SVG.";
    }
    return file.size > MAX_IMAGE_BYTES ? "Die Datei ist größer als 16 MB." : null;
}
/** A picture needs its alternative text at the upload; a video and a caption file do not. */
export function needsAltText(file, accepts) {
    return accepts !== "captions" && !isVideoFile(file);
}
/** The refusal of a picture without its description; the editor then focuses that field. */
export const ALT_TEXT_MISSING = "Die Bildbeschreibung fehlt.";
const NO_FILE = {
    picture: "Bitte zuerst eine Bilddatei wählen.",
    slide: "Bitte zuerst ein Bild oder ein Video wählen.",
    captions: "Bitte zuerst eine Untertiteldatei wählen.",
};
/**
 * Why an upload cannot start yet: no file, a file the server will refuse, or no
 * alternative text, which the server asks for at the moment of the upload (9.6.5).
 */
export function uploadProblem(file, altText, accepts = "picture") {
    if (file === null) {
        return NO_FILE[accepts];
    }
    const problem = fileProblem(file, accepts);
    if (problem !== null) {
        return problem;
    }
    return needsAltText(file, accepts) && altText.trim() === "" ? ALT_TEXT_MISSING : null;
}
/** The verdict of the security check, as the end of a line: " (geprüft)". */
function verdict(file) {
    const status = file.scan_status ?? null;
    if (status === null) {
        return "";
    }
    return ` (${status === "clean" ? "geprüft" : (SCAN_STATUS_LABELS[status] ?? status)})`;
}
/**
 * One picture in words: its description and, unless `withVerdict` is false (the sponsor's
 * guided configurator), the verdict of the security check.
 */
export function pictureLine(picture, withVerdict = true) {
    const text = picture.alt_text ? `»${picture.alt_text}«` : "ohne Bildbeschreibung";
    return `${text}${withVerdict ? verdict(picture) : ""}`;
}
/**
 * Any file of the door in words, with its verdict: a picture by its alternative text, a video
 * or a caption file by what it is.
 */
export function fileLine(file, withVerdict = true) {
    const said = withVerdict ? verdict(file) : "";
    if (file.kind === "video") {
        return `Video${said}`;
    }
    if (file.kind === "document") {
        return `Untertiteldatei${said}`;
    }
    return `Bild: ${pictureLine(file, withVerdict)}`;
}
//# sourceMappingURL=door.js.map